/**
 * Formularios oficiales del MSP que Donna imprime ya llenos: el 033 (historia clínica única de odontología) y el 024 (consentimiento informado,
 * Acuerdo Ministerial 5316 de 2016: aceptación, negativa y revocatoria). Aquí vive lo que no depende de la pantalla: cómo se reparten los
 * nombres, qué casillas se marcan con los antecedentes y cómo se separan las partes de un consentimiento ya firmado.
 */
import { diagnosticosExamen, type AnamnesisEstructurada, type EstadoRegion } from "./historia.ts";
import { CIE10, descripcionCie } from "./cie10.ts";

/** «Apellidos» en primer y segundo apellido: el último es el segundo («De la Cruz Pérez» → «De la Cruz» y «Pérez»). */
export function partirApellidos(s: string): [string, string] {
  const w = s.trim().split(/\s+/).filter(Boolean);
  return w.length <= 1 ? [w[0] ?? "", ""] : [w.slice(0, -1).join(" "), w[w.length - 1]];
}
/** «Nombres» en primer y segundo nombre («María de los Ángeles» → «María» y «de los Ángeles»). */
export function partirNombres(s: string): [string, string] {
  const w = s.trim().split(/\s+/).filter(Boolean);
  return w.length <= 1 ? [w[0] ?? "", ""] : [w[0], w.slice(1).join(" ")];
}

/** Edad con su condición como la pide el formulario 033: años (A), meses (M) o días (D). */
export function edadConCondicion(nacimiento: string | null, hoy: string): { valor: number; condicion: "A" | "M" | "D" } | null {
  if (!nacimiento || !/^\d{4}-\d{2}-\d{2}$/.test(nacimiento) || nacimiento > hoy) return null;
  const n = new Date(nacimiento + "T12:00:00Z"), h = new Date(hoy + "T12:00:00Z");
  let meses = (h.getUTCFullYear() - n.getUTCFullYear()) * 12 + (h.getUTCMonth() - n.getUTCMonth()) - (h.getUTCDate() < n.getUTCDate() ? 1 : 0);
  if (meses >= 12) return { valor: Math.floor(meses / 12), condicion: "A" };
  if (meses >= 1) return { valor: meses, condicion: "M" };
  return { valor: Math.round((h.getTime() - n.getTime()) / 86400000), condicion: "D" };
}

/** Las casillas de «Antecedentes personales y familiares» del 033, marcadas con lo que está en la anamnesis de Donna. */
export const CASILLAS_ANTECEDENTES = [
  "Alergia a antibiótico", "Alergia a anestesia", "Hemorragias", "VIH / SIDA", "Tuberculosis", "Asma", "Diabetes", "Hipertensión arterial", "Enfermedad cardíaca", "Otro",
] as const;

export function antecedentes033(a: Pick<AnamnesisEstructurada, "app" | "alg" | "app_det" | "alg_otra" | "fam" | "fam_det" | "medicacion">): { marcadas: Record<string, boolean>; descripcion: string } {
  const app = new Set(a.app), alg = new Set(a.alg);
  const marcadas: Record<string, boolean> = {
    "Alergia a antibiótico": ["Penicilina", "Cefalosporinas", "Sulfas"].some((x) => alg.has(x)),
    "Alergia a anestesia": alg.has("Anestésicos locales"),
    "Hemorragias": app.has("Trastornos de coagulación") || app.has("Anticoagulantes"),
    "VIH / SIDA": app.has("VIH"), "Tuberculosis": app.has("Tuberculosis"), "Asma": app.has("Asma"), "Diabetes": app.has("Diabetes"),
    "Hipertensión arterial": app.has("Hipertensión arterial"), "Enfermedad cardíaca": app.has("Cardiopatía"),
  };
  const propios = new Set(["Diabetes", "Hipertensión arterial", "Cardiopatía", "Trastornos de coagulación", "Anticoagulantes", "Asma", "VIH", "Tuberculosis"]);
  const otrosApp = a.app.filter((x) => !propios.has(x));
  const otrasAlg = a.alg.filter((x) => !["Penicilina", "Cefalosporinas", "Sulfas", "Anestésicos locales"].includes(x));
  const partes = [
    otrosApp.length || a.app_det ? `Personales: ${[...otrosApp, a.app_det].filter(Boolean).join("; ")}` : "",
    otrasAlg.length || a.alg_otra ? `Alergias: ${[...otrasAlg, a.alg_otra].filter(Boolean).join("; ")}` : "",
    a.medicacion ? `Medicación: ${a.medicacion}` : "",
  ].filter(Boolean);
  marcadas["Otro"] = partes.length > 0;
  return { marcadas, descripcion: partes.join(" · ") };
}

/** Las casillas de «Antecedentes patológicos familiares» del 033. */
export const CASILLAS_FAMILIARES = [
  "Cardiopatía", "Hipertensión arterial", "Enf. cardiovascular", "Endócrino metabólico", "Cáncer", "Tuberculosis", "Enf. mental", "Enf. infecciosa", "Mal formación", "Otro",
] as const;

export function familiares033(a: Pick<AnamnesisEstructurada, "fam" | "fam_det">): { marcadas: Record<string, boolean>; descripcion: string } {
  const fam = new Set(a.fam);
  const marcadas: Record<string, boolean> = {
    "Cardiopatía": fam.has("Cardiopatía"), "Hipertensión arterial": fam.has("Hipertensión arterial"), "Enf. cardiovascular": fam.has("Enf. cardiovascular"),
    "Endócrino metabólico": fam.has("Diabetes") || fam.has("Endócrino metabólico") || fam.has("Hipertiroidismo") || fam.has("Hipotiroidismo"), "Cáncer": fam.has("Cáncer"), "Tuberculosis": fam.has("Tuberculosis"),
    "Enf. mental": fam.has("Enf. mental"), "Enf. infecciosa": fam.has("Enf. infecciosa"),
    "Mal formación": fam.has("Labio o paladar fisurado") || fam.has("Mal formación"),
  };
  const propias = ["Cardiopatía", "Hipertensión arterial", "Enf. cardiovascular", "Diabetes", "Endócrino metabólico", "Hipertiroidismo", "Hipotiroidismo", "Cáncer", "Tuberculosis", "Enf. mental", "Enf. infecciosa", "Labio o paladar fisurado", "Mal formación"];
  const otros = a.fam.filter((x) => !propias.includes(x));
  const partes = [...otros, a.fam_det].filter(Boolean);
  marcadas["Otro"] = partes.length > 0;
  return { marcadas, descripcion: partes.join("; ") };
}

/* ---------- formulario 024: consentimiento informado ---------- */

/** Texto de la declaratoria del modelo oficial (Acuerdo Ministerial 5316, 7.13 g). */
export const TEXTO_DECLARATORIA_024 =
  "He facilitado la información completa que conozco, y me ha sido solicitada, sobre los antecedentes personales, familiares y de mi estado de salud. Soy consciente de que omitir estos datos puede afectar los resultados del tratamiento. " +
  "Estoy de acuerdo con el procedimiento que se me ha propuesto; he sido informado de las ventajas e inconvenientes del mismo; se me ha explicado de forma clara en qué consiste, los beneficios y posibles riesgos del procedimiento. " +
  "He escuchado, leído y comprendido la información recibida y se me ha dado la oportunidad de preguntar sobre el procedimiento. He tomado consciente y libremente la decisión de autorizar el procedimiento. " +
  "Consiento que durante la intervención, me realicen otro procedimiento adicional, si es considerado necesario según el juicio del profesional de la salud, para mi beneficio. También conozco que puedo retirar mi consentimiento cuando lo estime oportuno.";
/** Negativa (7.13 h). */
export const TEXTO_NEGATIVA_024 =
  "Una vez que he entendido claramente el procedimiento propuesto, así como las consecuencias posibles si no se realiza la intervención, no autorizo y me niego a que se me realice el procedimiento propuesto y libero de responsabilidades futuras de cualquier índole al establecimiento de salud y al profesional sanitario que me atiende, por no realizar la intervención sugerida.";
/** Revocatoria (7.13 i); las fechas se llenan en el formulario. */
export const TEXTO_REVOCATORIA_024 =
  "De forma libre y voluntaria, revoco el consentimiento realizado en fecha {fecha} y manifiesto expresamente mi deseo de no continuar con el procedimiento médico que doy por finalizado en esta fecha: {hoy}. Libero de responsabilidades futuras de cualquier índole al establecimiento de salud y al profesional sanitario que me atiende.";

export interface PartesConsentimiento {
  numero: number | null; titulo: string; procedimientos: string[];
  /** en qué consiste, cómo se realiza y cuánto dura (el compendio los trae juntos en «Naturaleza del procedimiento») */
  naturaleza: string; como: string; duracion: string;
  objetivos: string;
  /** todos los riesgos del procedimiento, y separados en frecuentes (poco graves) y poco frecuentes (graves) cuando el texto firmado los separa */
  riesgos: string[]; riesgosFrecuentes: string[]; riesgosPocoFrecuentes: string[];
  personales: string[]; alternativas: string; manejo: string; consecuencias: string;
  /** anexos del consentimiento especializado y cláusula de datos personales + declaraciones, tal como se firmaron */
  adicional: string;
}

/**
 * Separa un consentimiento ya firmado en las partes del formulario 024. Si el texto no tiene el formato esperado, las partes quedan vacías (se imprime el texto completo).
 * Los consentimientos firmados antes de que el texto trajera cómo se realiza, duración, manejo y consecuencias siguen leyéndose: esas partes quedan vacías.
 */
export function partesDeConsentimiento(texto: string): PartesConsentimiento {
  const bloques = texto.split(/\n\n+/);
  const buscar = (re: RegExp) => bloques.findIndex((b) => re.test(b));
  const cuerpo = (i: number) => (i < 0 ? "" : bloques[i].split("\n").slice(1).join("\n").trim());
  const iTitulo = buscar(/^CONSENTIMIENTO INFORMADO/), iProc = buscar(/^Procedimientos del plan:/);
  const iNat = buscar(/^1\. Naturaleza/), iObj = buscar(/^2\. Objetivos/), iRie = buscar(/^3\. Riesgos/), iAlt = buscar(/^4\. Alternativas/), iMan = buscar(/^5\. Manejo Posterior/);
  const titulo = iTitulo >= 0 ? bloques[iTitulo].split("\n").slice(1).join(" ").trim() : "";
  const numero = iTitulo >= 0 ? Number(bloques[iTitulo].match(/N°\s*(\d+)/)?.[1]) || null : null;
  // naturaleza: lo que va antes de «Cómo se realiza:» y «Duración estimada:»
  let naturaleza = "", como = "", duracion = "";
  for (const l of cuerpo(iNat).split("\n")) {
    const c = l.match(/^Cómo se realiza:\s*(.*)$/), d = l.match(/^Duración estimada:\s*(.*)$/);
    if (c) como = c[1].trim(); else if (d) duracion = d[1].trim(); else naturaleza = naturaleza ? `${naturaleza}\n${l}` : l;
  }
  // riesgos: frecuentes, poco frecuentes y los propios del paciente («Según sus antecedentes»)
  const lineas = cuerpo(iRie).split("\n").map((l) => l.trim()).filter(Boolean);
  const limpio = (l: string) => l.replace(/^•\s*/, "");
  const riesgosFrecuentes: string[] = [], riesgosPocoFrecuentes: string[] = [], personales: string[] = [];
  let seccion: "f" | "p" | "s" = "f";
  for (const l of lineas) {
    if (/^Riesgos frecuentes/.test(l)) seccion = "f";
    else if (/^Riesgos poco frecuentes/.test(l)) seccion = "p";
    else if (/^Según sus antecedentes:/.test(l)) seccion = "s";
    else (seccion === "f" ? riesgosFrecuentes : seccion === "p" ? riesgosPocoFrecuentes : personales).push(limpio(l));
  }
  const iDespues = Math.max(iAlt, iMan) >= 0 ? Math.max(iAlt, iMan) + 1 : -1;
  const adicional = iDespues >= 0 ? bloques.slice(iDespues).join("\n\n").trim() : "";
  // manejo posterior y consecuencias de no realizarlo
  let manejo = "", consecuencias = "";
  for (const l of cuerpo(iMan).split("\n")) {
    const m = l.match(/^Manejo posterior:\s*(.*)$/), c = l.match(/^Si no se realiza el procedimiento:\s*(.*)$/);
    if (m) manejo = m[1].trim(); else if (c) consecuencias = c[1].trim();
  }
  return {
    numero, titulo, procedimientos: iProc >= 0 ? bloques[iProc].replace(/^Procedimientos del plan:\s*/, "").replace(/\.$/, "").split(/;\s*/).filter(Boolean) : [],
    naturaleza, como, duracion, objetivos: cuerpo(iObj), riesgos: [...riesgosFrecuentes, ...riesgosPocoFrecuentes], riesgosFrecuentes, riesgosPocoFrecuentes,
    personales, alternativas: cuerpo(iAlt), manejo, consecuencias, adicional,
  };
}

/* ---------- sección N del 033: diagnósticos de la ficha ---------- */

export interface DxFicha { cie: string; texto: string; donde: string }
/** Los diagnósticos con CIE-10 de la ficha: los del odontograma y los del examen estomatognático, sin repetir (los mismos de la sección N del formulario 033). */
export function diagnosticosFicha(
  hallazgos: { diente: number | null; superficies: string[]; cie10: string | null; nota: string }[], regiones: Record<string, EstadoRegion>,
): DxFicha[] {
  const dx: DxFicha[] = [], ya = new Set<string>();
  for (const h of hallazgos) {
    if (!h.cie10) continue;
    const k = `${h.cie10}|${h.diente ?? ""}|${h.nota}`;
    if (ya.has(k)) continue;
    ya.add(k);
    dx.push({ cie: h.cie10, texto: CIE10[h.cie10] ?? descripcionCie(h.cie10), donde: h.diente ? `pieza ${h.diente}${h.superficies.length ? ` (${h.superficies.join("")})` : ""}` : "general" });
  }
  for (const d of diagnosticosExamen(regiones)) {
    const k = `${d.cie10}|${d.region}`;
    if (ya.has(k)) continue;
    ya.add(k);
    dx.push({ cie: d.cie10, texto: CIE10[d.cie10] ?? d.detalle, donde: d.region });
  }
  return dx;
}

/* ---------- diagnósticos que corresponden a cada consentimiento (formulario 024) ---------- */

/** A qué consentimiento pertenece un diagnóstico, según su código CIE-10 (cada consentimiento muestra solo los de su especialidad). */
const CIE_POR_CONSENTIMIENTO: Readonly<Record<string, RegExp>> = {
  general: /^(K00\.[2-5]|K02|K03\.[0-9]|K06\.0|S02\.5)/,
  endodoncia: /^K04\.[0-8]/,
  cirugia: /^(K00\.1|K01|K04\.[678]|K08\.3|K09|K10|K11\.[56]|K13\.[24])/,
  ortodoncia: /^(K07\.[0-5]|K00\.6)/,
  periodoncia_implantologia: /^(K05|K06|K08\.[12]|A69\.1)/,
  rehabilitacion: /^(K03\.[0-37]|K07\.6|K08\.[12]|S02\.5|K00\.[24])/,
  implantes: /^K08\.[12]/,
  cirugia_periodontal: /^(K05|K06)/,
  blanqueamiento: /^(K03\.7|K00\.3)/,
  expansion_maxilar: /^K07\.[0-5]/,
  ferula_atm: /^(K07\.6|K03\.[01])/,
};
/** Lo que se planificó sobre la pieza también la lleva a una especialidad (una pieza con extracción indicada va a cirugía, no a odontología general). */
const TIPO_POR_CONSENTIMIENTO: Readonly<Record<string, readonly string[]>> = {
  cirugia: ["extraccion", "incluido", "supernumerario", "quiste", "apicectomia", "frenectomia", "alveoloplastia", "traccion"],
  endodoncia: ["endodoncia", "pulpectomia"],
  periodoncia_implantologia: ["implante"],
  implantes: ["implante"],
  rehabilitacion: ["corona", "puente", "removible", "protesis_total", "perno", "incrustacion", "carilla"],
  odontopediatria: ["pulpotomia", "coronaacero", "mantenedor"],
};
const CONSENTIMIENTOS_CON_TIPO = Object.keys(TIPO_POR_CONSENTIMIENTO);
const esTemp = (n: number | null) => n !== null && ((n >= 51 && n <= 55) || (n >= 61 && n <= 65) || (n >= 71 && n <= 75) || (n >= 81 && n <= 85));

/**
 * Los diagnósticos con CIE-10 que van en el formulario 024 de un consentimiento: solo los de su especialidad.
 * Cirugía, endodoncia, ortodoncia, periodoncia e implantes, rehabilitación y odontología general toman los suyos por código CIE-10 y por lo planificado sobre la pieza;
 * odontopediatría, los de piezas temporales; sedación, los de las piezas de sus procedimientos.
 */
export function diagnosticosDeConsentimiento(
  tipo: string,
  hallazgos: { diente: number | null; superficies: string[]; cie10: string | null; nota: string; tipo?: string; estado?: string }[],
  regiones: Record<string, EstadoRegion>,
  procedimientos: string[] = [],
): DxFicha[] {
  const planificado = (d: number | null) => (d === null ? [] : hallazgos.filter((h) => h.diente === d && h.estado !== "realizado").map((h) => h.tipo ?? ""));
  const especial = (d: number | null) => CONSENTIMIENTOS_CON_TIPO.filter((c) => planificado(d).some((t) => TIPO_POR_CONSENTIMIENTO[c].includes(t)));
  const piezasDelPlan = new Set(procedimientos.flatMap((p) => [...p.matchAll(/pieza (\d{2})/g)].map((m) => Number(m[1]))));
  const corresponde = (cie: string, diente: number | null): boolean => {
    if (tipo === "odontopediatria") return esTemp(diente) || (diente !== null && especial(diente).includes("odontopediatria"));
    if (["sedacion", "sedacion_consciente", "anestesia_general", "anestesia_local"].includes(tipo)) return diente !== null && piezasDelPlan.has(diente);
    // lo planificado sobre la pieza la lleva a su especialidad; odontología general solo toma lo que no tiene una especialidad planificada
    const porTipo = especial(diente).filter((c) => c !== "odontopediatria");
    if (porTipo.includes(tipo)) return true;
    if (tipo === "general" && porTipo.length) return false;
    if (esTemp(diente) && tipo !== "odontopediatria") return false; // las piezas temporales van con odontopediatría
    return CIE_POR_CONSENTIMIENTO[tipo]?.test(cie) ?? false;
  };
  const out: DxFicha[] = [], ya = new Set<string>();
  for (const h of hallazgos) {
    if (!h.cie10 || !corresponde(h.cie10, h.diente)) continue;
    const k = `${h.cie10}|${h.diente ?? ""}|${h.nota}`;
    if (ya.has(k)) continue;
    ya.add(k);
    out.push({ cie: h.cie10, texto: CIE10[h.cie10] ?? descripcionCie(h.cie10), donde: h.diente ? `pieza ${h.diente}${h.superficies.length ? ` (${h.superficies.join("")})` : ""}` : "general" });
  }
  if (CIE_POR_CONSENTIMIENTO[tipo])
    for (const d of diagnosticosExamen(regiones)) {
      if (!CIE_POR_CONSENTIMIENTO[tipo]?.test(d.cie10)) continue;
      const k = `${d.cie10}|${d.region}`;
      if (ya.has(k)) continue;
      ya.add(k);
      out.push({ cie: d.cie10, texto: CIE10[d.cie10] ?? d.detalle, donde: d.region });
    }
  return out;
}
